/* eslint-disable no-param-reassign */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { auth } from '~/api/axios';
import { getProfiles } from '~/api/onboarding';
import { SOLUTION_LIST } from '~/env';
import { RootState, AppThunk } from '~/store';
import { Profile } from '~/types';
import { getPermissions } from '~/utils/permissions';
import { notAuthorized } from '~/routes';
import { history } from '~/index';

type PermissionsReturn = ReturnType<typeof getPermissions>;

export type State = {
  profiles: {
    data: Profile[] | null;
    loading: boolean;
    error: any;
  };
  permissions: { loading: boolean } & PermissionsReturn;
};

const initialState: State = {
  profiles: {
    data: null,
    loading: false,
    error: null,
  },
  permissions: {
    roles: [],
    permissions: {},
    loading: false,
  },
};

const slice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    loadProfilesBegin: (state) => {
      state.profiles = { loading: true, data: null, error: null };
    },
    loadProfilesSuccess: (state, action: PayloadAction<Profile[]>) => {
      state.profiles = { data: action.payload, loading: false, error: null };
    },
    loadProfilesFail: (state, action: PayloadAction<any>) => {
      state.profiles.error = action.payload;
      state.profiles.loading = false;
    },
    loadPermissionsBegin: (state) => {
      state.permissions = { ...initialState.permissions, loading: true };
    },
    loadPermissionsSuccess: (state, action: PayloadAction<PermissionsReturn>) => {
      state.permissions = { ...action.payload, loading: false };
    },
    loadPermissionsFail: (state) => {
      state.permissions = { ...initialState.permissions };
    },
  },
});

export const {
  loadProfilesSuccess,
  loadProfilesBegin,
  loadProfilesFail,
  loadPermissionsBegin,
  loadPermissionsSuccess,
  loadPermissionsFail,
} = slice.actions;

export const loadProfiles = (onReady: () => void): AppThunk => {
  return async (dispatch, getState) => {
    const { loading } = getState().ui.profiles;
    console.log(`loading=${loading}`);
    if (loading) return;
    try {
      dispatch(loadProfilesBegin());
      const { data } = await getProfiles();
      const preferredProfileId = auth.getProfile();

      console.log(
        `initializing UI; checking if there is any business profile allowed for this portal; preferred profile is ${preferredProfileId}`
      );
      console.log(`initializing UI; allowed solutions:`, SOLUTION_LIST);
      console.log(`initializing UI; loaded profiles:`, data);
      const availableProfiles = data.filter((item) => {
        return item.type === 'BUSINESS' && SOLUTION_LIST.indexOf(item.solutionId!) >= 0;
      });
      console.log(
        `initializing UI; found ${availableProfiles.length} available profiles:`,
        availableProfiles
      );
      const businessProfile =
        (preferredProfileId
          ? availableProfiles.find((item) => item.id === preferredProfileId)
          : null) || availableProfiles[0];
      console.log(`initializing UI; current profile is ${businessProfile?.id}`);

      // set default profile
      if (businessProfile?.id && businessProfile?.solutionId) {
        auth.setProfile(businessProfile.id);
        auth.setSolution(businessProfile.solutionId);
        dispatch(loadProfilesSuccess(data));
        onReady();
      } else {
        dispatch(loadProfilesFail("You're unauthorized to use this portal"));
        history.push(`/${notAuthorized}`);
      }
    } catch (error: any) {
      dispatch(loadProfilesFail(error.message));
    }
  };
};

export const selectProfiles = (state: RootState): State['profiles'] => state.ui.profiles;
export const selectPermissions = (state: RootState): State['permissions'] => state.ui.permissions;

export default slice.reducer;
